import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { callRpc } from '@/services/juntasRpc'
import { ONBOARDING_FAMILY_FLAG } from '@/utils/onboardingProgress'

const PROFILE_COLUMNS = `
  name, age, languages, occupation, presentation,
  has_children, children_ages, accepts_living_with_children,
  work_schedule, remote_work, sleep_time_start, sleep_time_end,
  lifestyle, preferred_zones, budget_min, budget_max, cohabitation_size,
  trans_preference, gender_identity, identity_status, profile_completed,
  verification_method, account_type
`.replace(/\s+/g, ' ')

async function requireUser() {
  if (!isSupabaseConfigured || !supabase) {
    return { user: null, error: new Error('SUPABASE_NOT_CONFIGURED') }
  }
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser()
  if (error) return { user: null, error }
  if (!user) return { user: null, error: new Error('NOT_AUTHENTICATED') }
  return { user, error: null }
}

/** Lee la videollamada Vía C propia (RLS: solo la dueña o moderación). */
export async function fetchMyViaCSession(sessionId) {
  if (!supabase) return { data: null, error: new Error('SUPABASE_NOT_CONFIGURED') }
  return supabase
    .from('trans_via_c_sessions')
    .select('id, scheduled_at, meeting_url, status')
    .eq('id', sessionId)
    .maybeSingle()
}

/** La usuaria cancela su propia videollamada (desde el link del email). */
export async function cancelMyViaCSession(sessionId) {
  return callRpc('cancel_my_via_c_session', { p_session_id: sessionId })
}

export async function fetchMyProfile() {
  if (!isSupabaseConfigured || !supabase) return { data: null, error: null }

  const { user, error: authError } = await requireUser()
  if (authError) return { data: null, error: authError }

  return supabase.from('profiles').select(PROFILE_COLUMNS).eq('id', user.id).maybeSingle()
}

async function upsertProfile(patch) {
  const { user, error: authError } = await requireUser()
  if (authError) return { data: null, error: authError }

  return supabase.from('profiles').upsert(
    {
      id: user.id,
      ...patch,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  )
}

export async function saveOnboardingStep1({ name, age, languages, occupation, presentation }) {
  return upsertProfile({
    name: name.trim(),
    age,
    languages,
    occupation: occupation.trim(),
    presentation: presentation?.trim() || null,
  })
}

export async function saveOnboardingStep2({
  hasChildren,
  childrenAges,
  acceptsLivingWithChildren,
  existingLifestyle = {},
}) {
  return upsertProfile({
    has_children: hasChildren,
    children_ages: childrenAges,
    accepts_living_with_children: acceptsLivingWithChildren,
    lifestyle: {
      ...existingLifestyle,
      [ONBOARDING_FAMILY_FLAG]: true,
    },
  })
}

export async function saveOnboardingStep3({
  workSchedule,
  remoteWork,
  sleepTimeStart,
  sleepTimeEnd,
  existingLifestyle = {},
}) {
  return upsertProfile({
    work_schedule: workSchedule,
    remote_work: remoteWork,
    sleep_time_start: sleepTimeStart,
    sleep_time_end: sleepTimeEnd,
    lifestyle: existingLifestyle,
  })
}

export async function saveOnboardingStep4(lifestyle) {
  const { [ONBOARDING_FAMILY_FLAG]: familyFlag, ...rest } = lifestyle
  return upsertProfile({
    lifestyle: {
      ...rest,
      ...(familyFlag ? { [ONBOARDING_FAMILY_FLAG]: familyFlag } : {}),
    },
  })
}

export async function saveOnboardingStep5({
  preferredZones,
  budgetMin,
  budgetMax,
  cohabitationSize,
  existingLifestyle = {},
}) {
  return upsertProfile({
    preferred_zones: preferredZones,
    budget_min: budgetMin,
    budget_max: budgetMax,
    cohabitation_size: cohabitationSize,
    lifestyle: existingLifestyle,
  })
}

export async function saveOnboardingStep6Meta({
  genderIdentity,
  verificationMethod,
  existingLifestyle = {},
}) {
  const methodMap = {
    A_dni: 'dni',
    B_carta_asociacion: 'association_letter',
    C_videollamada: 'video_call',
  }

  return upsertProfile({
    gender_identity: genderIdentity,
    verification_method: methodMap[verificationMethod] ?? null,
    lifestyle: existingLifestyle,
  })
}

export async function saveOnboardingStep7Complete({ existingLifestyle = {} }) {
  return upsertProfile({
    profile_completed: true,
    lifestyle: existingLifestyle,
  })
}

// Edición de perfil tras la creación. NO toca la identidad verificada
// (name, age, gender_identity, identity_status, is_trans): solo los campos
// de preferencias / estilo de vida que la usuaria puede cambiar libremente.
// Identidad de género: auto-identificación PRIVADA (solo visible para moderación
// KYC, nunca en feeds/matching). Editable por la propia usuaria sin re-verificación
// — la elegibilidad «solo mujeres» no cambia entre las opciones. La declaración
// trans (is_trans) se gestiona aparte vía set_trans_status (con consentimiento).
export async function setGenderIdentity(genderIdentity) {
  return upsertProfile({ gender_identity: genderIdentity })
}

export async function saveProfileEdits({
  languages,
  occupation,
  presentation,
  hasChildren,
  childrenAges,
  acceptsLivingWithChildren,
  workSchedule,
  remoteWork,
  sleepTimeStart,
  sleepTimeEnd,
  preferredZones,
  budgetMin,
  budgetMax,
  cohabitationSize,
  lifestyle,
}) {
  return upsertProfile({
    languages,
    occupation: occupation?.trim() ?? '',
    presentation: presentation?.trim() || null,
    has_children: hasChildren,
    children_ages: childrenAges,
    accepts_living_with_children: acceptsLivingWithChildren,
    work_schedule: workSchedule,
    remote_work: remoteWork,
    sleep_time_start: sleepTimeStart,
    sleep_time_end: sleepTimeEnd,
    preferred_zones: preferredZones,
    budget_min: budgetMin,
    budget_max: budgetMax,
    cohabitation_size: cohabitationSize,
    lifestyle,
  })
}
